import { useState, useContext } from 'react';

import endpoints from '../../../../../../api/endpoints';
import { useDialog } from '../../../../../common/dialog/useDialog';
import { BusinessError } from '../../../../../common/business-error';
import { handleResponseError } from '../../../../../../api/handleResponseError';
import { ToastNotificationContext } from '../../../../../common/toast-notification';
import { addParams, fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';

import { UssdAppEditItem, UssdAppFormData } from './interfaces';
import { getEditAppBusinessError } from './businessErrors';

export type DialogError = BusinessError | null;

export const useEditApp = () => {
  const [initEditFormData, setInitEditFormData] = useState<UssdAppFormData>();
  const [isEditAppLoading, setEditAppLoading] = useState<boolean>(false);
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();
  const [error, setError] = useState<DialogError>(null);
  const { openToast } = useContext(ToastNotificationContext);

  const handleOpenEditAppDialog = (initUssdAppSettings: UssdAppFormData) => {
    setError(null);
    setInitEditFormData(initUssdAppSettings);
    handleOpenDialog();
  };

  const handleCloseEditAppDialog = () => {
    setError(null);
    handleCloseDialog();
  };

  const handleSubmitEditAppDialog = async (ussdAppEditFormData: UssdAppEditItem, onSuccess: () => void) => {
    if (ussdAppEditFormData) {
      setError(null);
      setEditAppLoading(true);

      try {
        const response = await fetchWithTokenRefresh(
          endpoints.editUssdAppSettings(ussdAppEditFormData.appId),
          addParams('PUT', ussdAppEditFormData)
        );

        if (response.status !== 204) {
          await handleResponseError(response);
        }

        onSuccess();
        setEditAppLoading(false);
        handleCloseEditAppDialog();
        openToast({
          message: `${ussdAppEditFormData.appId} app edited successfully`,
          severity: 'success'
        });
      } catch (e) {
        const { message, severity } = getEditAppBusinessError(e);

        setError({ message, severity });
        setEditAppLoading(false);
      }
    }
  };

  return {
    error,
    initEditFormData,
    isEditAppDialogOpen: isDialogOpen,
    isEditAppLoading,
    handleOpenEditAppDialog,
    handleCloseEditAppDialog,
    handleSubmitEditAppDialog
  };
};
